import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, Text } from '@/nova/components';
import { useClipboard } from '@/nova/components/CopyButton';
import { ROUTE } from '@/nova/routes';

import { linkAssetName } from '../../link-attribution';
import { destinationHostPath, versionStamp } from '../../tracking-link';
import type { TrackingLink, TrackingLinkKind } from '../../tracking-links-api';
import { isOwnLink, trackingKindLabel } from '../LandingPageComposer/link-for';
import {
  CopyBtn,
  CopyField,
  CopyUrl,
  FieldLabel,
  GoesTo,
  GoesToLabel,
  GoesToMeta,
  GoesToPage,
  GoesToValue,
  LinkSkeleton,
} from '../TrackingLink/TrackingLink.styles';
import * as S from './AssetLinksFooter.styles';

export interface AssetLinksFooterProps {
  kind: TrackingLinkKind;
  /** The asset's spark creatives id (a landing page: its spark id). */
  creativeId: string;
  /** The asset's links, newest version first (a landing page's own link first), as the sheet's breakdown orders them. */
  links: readonly TrackingLink[];
  linksStatus: 'loading' | 'ready' | 'error';
}

/** How many links show before "Show all". */
const VISIBLE_ROWS = 3;

interface LinkRowProps {
  link: TrackingLink;
  /** The row's label: a literal (`text`) or data (`raw`). */
  label: { text: string; raw?: boolean };
  /** The landing page this sheet is for, when it is one (its own page is not linked to itself). */
  pageId?: string;
}

/** One link, read-only: its label, the copyable address, then where it goes (as the composer's panel shows them). */
const LinkRow = ({ link, label, pageId }: LinkRowProps): React.ReactElement => {
  const { t } = useTranslation();
  const { copied, copy } = useClipboard();
  const studioId = link.destination_type === 'landing_page' ? link.landing_page_id : '';
  const where = destinationHostPath(link.landing_page?.url || link.destination_url);
  const pageName = link.landing_page?.name.trim() ?? '';
  return (
    <S.LinkRow>
      <FieldLabel raw={label.raw} text={label.text} />
      {link.url ? (
        <CopyField>
          <CopyUrl
            readOnly
            value={link.url}
            onFocus={(e) => e.currentTarget.select()}
            aria-label={label.raw ? label.text : t(label.text)}
          />
          <CopyBtn
            type="button"
            $copied={copied}
            onClick={() => void copy(link.url)}
            aria-label={copied ? t('Copied') : t('Copy link')}
            title={copied ? t('Copied') : t('Copy link')}
          >
            <Icon name={copied ? 'check' : 'copy'} size={16} />
          </CopyBtn>
        </CopyField>
      ) : null}
      <GoesTo>
        <GoesToLabel text="Goes to:" />
        {link.destination_type === 'casino' ? (
          <GoesToValue text="Casino" />
        ) : studioId && studioId !== pageId ? (
          <GoesToPage to={ROUTE.creativesLandingPage(studioId)} title={t('Open the landing page')}>
            {pageName ? <Text raw text={pageName} /> : <Text text="Landing page" />}
          </GoesToPage>
        ) : pageName ? (
          <GoesToValue raw text={pageName} />
        ) : (
          <GoesToValue text="Landing page" />
        )}
        {where ? <GoesToMeta raw text={where} /> : null}
      </GoesTo>
    </S.LinkRow>
  );
};

/**
 * The stats sheet's footer: the asset's tracking links, read-only, each the composer panel's copyable address (with
 * its copy button) and "Goes to: <casino | page, linked> · host/path". Several version links: one row each, labelled
 * with the version, newest first; a landing page's own unique link first, then the ads' links landing on it. The first
 * three show, the rest behind "Show all". No link yet: where to get one.
 */
export const AssetLinksFooter = ({
  kind,
  creativeId,
  links,
  linksStatus,
}: AssetLinksFooterProps): React.ReactElement | null => {
  const { t } = useTranslation();
  const [showAll, setShowAll] = React.useState(false);
  const landing = kind === 'landing_page';

  if (linksStatus === 'error') return null;
  if (linksStatus === 'loading') {
    return (
      <S.Root aria-hidden="true">
        <S.LinkRow>
          {landing ? <FieldLabel text="Unique tracking link" /> : <FieldLabel text="Tracking link" />}
          <LinkSkeleton />
        </S.LinkRow>
      </S.Root>
    );
  }
  if (links.length === 0) {
    return (
      <S.Root>
        {landing ? (
          <S.Hint text="No tracking link yet. Publish the page, then get its unique tracking link in the composer." />
        ) : (
          <S.Hint text="No tracking link yet. Get one in the composer, under the preview." />
        )}
      </S.Root>
    );
  }

  const labelOf = (link: TrackingLink): { text: string; raw?: boolean } => {
    if (landing) {
      if (isOwnLink(link, creativeId)) return { text: 'Unique tracking link' };
      return { text: `${trackingKindLabel(link.kind, t)} · ${linkAssetName(link)}`, raw: true };
    }
    if (links.length === 1) return { text: 'Tracking link' };
    return { text: versionStamp(link.version_key) || link.name, raw: true };
  };
  const shown = showAll ? links : links.slice(0, VISIBLE_ROWS);
  const hidden = links.length - shown.length;

  return (
    <S.Root>
      {shown.map((link) => (
        <LinkRow
          key={link.id}
          link={link}
          label={labelOf(link)}
          pageId={landing ? creativeId : undefined}
        />
      ))}
      {hidden > 0 ? (
        <S.ShowAll type="button" onClick={() => setShowAll(true)}>
          <Text tag="span" text="Show all {{n}} links" placeholders={{ n: links.length }} />
        </S.ShowAll>
      ) : null}
    </S.Root>
  );
};

export default AssetLinksFooter;
